/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderConfirmationViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { ReactElement } from 'react';

interface IExtendedOrderInformation extends IOrderInformation {
    salesOrder?: SalesOrder;
    moduleProps?: any;
}

interface IPostalAddress {
    Name?: string;
    Street?: string;
    StreetNumber?: string;
    ZipCode?: string;
    City?: string;
    ThreeLetterISORegionName?: string;
}

interface IAddressElementProps {
    address: IPostalAddress;
}

export const OrderConfirmationOrderInfomation: React.FC<IExtendedOrderInformation> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId,
    salesOrder,
    moduleProps
}) => (
    <>
        <Node className={''}>{receiptEmail}</Node>

        {createdDate}
        <Node {...orderInformationProps}>
            {salesOrder?.Comment && (
                <span className='ms-order-confirmation__order-information-channel-reference-id'>
                    {moduleProps.resources.orderConfirmationViewCustomerOrderNumberLabel} <span>{salesOrder.Comment}</span>
                </span>
            )}
            {salesOrder?.CustomerRequisition && (
                <span className='ms-order-confirmation__order-information-channel-reference-id'>
                    {moduleProps.resources.orderConfirmationViewCustomerRequisitionLabel} <span>{salesOrder.CustomerRequisition}</span>
                </span>
            )}
            {salesOrder?.Notes?.values().next().value?.Description && (
                <span className='ms-order-confirmation__order-information-channel-reference-id'>
                    {moduleProps.resources.orderConfirmationViewCustomerNoteLabel}{' '}
                    <span>{salesOrder.Notes?.values().next().value?.Description}</span>
                </span>
            )}
            {channelReferenceId}
        </Node>
    </>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, address, pickupDateTimeslot }) => {
    const addressElement = address as ReactElement<IAddressElementProps> | undefined;
    const addressData = addressElement?.props.address;

    return (
        <Node {...deliveryProps}>
            {heading}

            {addressData && (
                <>
                    <div>{addressData.Name}</div>
                    <div>
                        {addressData.Street} {addressData.StreetNumber}
                    </div>
                    <div>
                        {addressData.ZipCode} {addressData.City}
                    </div>
                    <div>{addressData.ThreeLetterISORegionName}</div>
                </>
            )}

            {pickupDateTimeslot}
        </Node>
    );
};

export const OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {delivery && delivery.shippingItemsToYou}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine: ISalesLine) => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderConfirmationGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => {
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {tax}

            <div className='mms-order-confirmation__order-summary-line-total-amount'>{totalAmount}</div>
            {earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps> = ({
    moduleProps,
    heading,
    backToShoppingLink,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    salesOrder
}) => {
    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}

            {orderInfomation && (
                <OrderConfirmationOrderInfomation {...orderInfomation} salesOrder={salesOrder} moduleProps={moduleProps.moduleProps} />
            )}
            {backToShoppingLink}
            {groups && <OrderConfirmationGroups {...groups} />}
            {payment && <OrderConfirmationPayment {...payment} />}
            {orderSummary && <OrderConfirmationOrderSummary {...orderSummary} />}
            {help && <OrderConfirmationHelp {...help} />}
        </Module>
    );
};

export default OrderConfirmationView;
